import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AppContext } from '../context';
import { gcoinRequest } from '../gcoinApi';

export default function useGCoinRead(path, { poll = false, keepPreviousData = false } = {}) {
    const { token } = useContext(AppContext);
    const [state, setState] = useState({ data: null, path: null, token: null, error: '', requestPath: null, loading: false });
    const seq = useRef(0);
    const abort = useRef(null);
    const inFlight = useRef(false);
    const refresh = useCallback(async () => {
        if (!path || !token) return;
        abort.current?.abort();
        const controller = new AbortController(); abort.current = controller;
        const id = ++seq.current;
        inFlight.current = true;
        setState(prev => ({ ...prev, data: prev.token === token ? prev.data : null,
            path: prev.token === token ? prev.path : null, token, error: '', requestPath: path, loading: true }));
        try {
            const data = await gcoinRequest(token, path, { signal: controller.signal });
            if (id === seq.current) setState({ data, path, token, requestPath: path, error: '', loading: false });
        } catch (err) {
            if (!controller.signal.aborted && id === seq.current) {
                setState(prev => ({ ...prev, requestPath: path, error: err.message || 'Не удалось обновить данные.', loading: false }));
            }
        } finally {
            if (id === seq.current) inFlight.current = false;
        }
    }, [path, token]);
    useEffect(() => {
        refresh();
        return () => { ++seq.current; inFlight.current = false; abort.current?.abort(); };
    }, [refresh]);
    useEffect(() => {
        if (!poll) return undefined;
        const timer = setInterval(() => { if (!document.hidden && !inFlight.current) refresh(); }, 30000);
        return () => clearInterval(timer);
    }, [refresh, poll]);

    // Предыдущие данные сохраняются только по явному выбору страницы и только
    // в рамках текущего токена. Профиль другого человека не наследует старый ответ.
    const sameOwner = !!token && state.token === token;
    const data = path && sameOwner && (state.path === path || keepPreviousData) ? state.data : null;
    const error = sameOwner && state.requestPath === path ? state.error : '';
    const loading = !!(path && token && (state.loading && sameOwner || (!error && (!sameOwner || state.requestPath !== path))));
    return { data, error, loading, isPreviousData: !!data && state.path !== path, refresh };
}
